import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { AuthHeading, AuthLayout } from '@/features/auth/components/auth-layout'
import { loginOptionsQuery, theOnlyLoginOption } from '@/features/auth/queries'
import { signInUrl } from '@/features/auth/sign-in-url'
import { NetworkError } from '@/api/errors'
import { Spinner } from '@/shared/ui/spinner'
import { backendUrl } from '@/shared/config/runtime'

/**
 * Not a page: the hop between having no session and the identity provider.
 *
 * There is one way in, so a screen offering it is a screen asking somebody to
 * confirm the only thing that could happen next. The provider's url is the one
 * thing that has to be fetched before the browser can be forwarded, which is the
 * whole reason this renders at all.
 *
 * It says something only when it cannot forward: an unreachable backend, or an
 * instance with no provider configured. Both are deployment faults, and both
 * would otherwise leave somebody looking at a blank page.
 */
export function LoginPage() {
  const { t } = useTranslation()
  const { original } = useSearch({ from: '/login' })

  // A misconfigured `PUBLIC_BACKEND_URL` makes every request fail with no clue as
  // to why, and this is where every such instance lands. Read off the options
  // request rather than probed separately: that request has already proved
  // whether the backend answers, so a second one could only disagree.
  const { data, error, isPending } = useQuery(loginOptionsQuery())
  const option = theOnlyLoginOption(data)
  const destination = option === null ? null : signInUrl(option.redirect_url, original)

  useEffect(() => {
    if (destination === null) return
    // `replace` rather than an assignment, so the back button out of the provider
    // leads wherever the person came from instead of back to this hop, which
    // would forward them again.
    window.location.replace(destination)
  }, [destination])

  if (isPending || destination !== null) {
    return (
      <main className="app-background flex min-h-dvh items-center justify-center p-4">
        <Spinner
          className="text-muted-foreground size-6"
          aria-label={t('login.signingIn')}
        />
      </main>
    )
  }

  return (
    <AuthLayout>
      {error instanceof NetworkError ? (
        <AuthHeading
          alert
          eyebrow={t('login.unavailableEyebrow')}
          heading={t('login.backendUnreachableTitle')}
        >
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t('login.backendUnreachableDescription', {
              backendUrl: new URL(backendUrl()).origin,
              browserUrl: window.location.origin,
            })}
          </p>
          {/* The one page that says what this deployment was configured with and
              whether the address answers. It is unguarded, which is what makes it
              reachable from here. */}
          <Link to="/debug" className="text-primary text-sm underline underline-offset-4">
            {t('login.checkDeployment')}
          </Link>
        </AuthHeading>
      ) : (
        <AuthHeading
          alert
          eyebrow={t('login.unavailableEyebrow')}
          heading={t('login.notConfiguredTitle')}
        >
          <p className="text-muted-foreground text-sm leading-relaxed">
            {t('login.notConfiguredDescription')}
          </p>
        </AuthHeading>
      )}
    </AuthLayout>
  )
}
